import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import type { InventoryMovementKind, StockLocation } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useInventoryMovements } from '../hooks/use-inventory-movements';

import { MovementList } from './movement-list';

const ALL = '__all';
const KINDS: readonly InventoryMovementKind[] = [
  'receive',
  'adjust',
  'count',
  'reserve',
  'release',
  'sell',
  'return',
];

export interface ItemHistoryCardProps {
  storeId: string;
  variantId: string;
  locations: readonly StockLocation[];
  locationId: string | null;
  kind: InventoryMovementKind | null;
  onLocationChange: (locationId: string | null) => void;
  onKindChange: (kind: InventoryMovementKind | null) => void;
}

/** The variant's ledger over every location, narrowed by location and kind of change. */
export function ItemHistoryCard({
  storeId,
  variantId,
  locations,
  locationId,
  kind,
  onLocationChange,
  onKindChange,
}: ItemHistoryCardProps) {
  const { t } = useTranslation('inventory');
  const query = useInventoryMovements(storeId, {
    variantId,
    ...(locationId ? { locationId } : {}),
    ...(kind ? { kind } : {}),
  });
  const movements = query.data?.pages.flatMap((page) => page.items) ?? [];
  const names = new Map(locations.map((location) => [location.id, location.name]));

  return (
    <Card className="gap-0 py-0" data-tour="inventory.item.history">
      <CardHeader className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1">
          <CardTitle className="text-sm">{t('item.historyTitle')}</CardTitle>
          <CardDescription>{t('item.historyDescription')}</CardDescription>
        </div>
        <div className="flex flex-wrap gap-2">
          {locations.length > 1 ? (
            <Select
              value={locationId ?? ALL}
              onValueChange={(value) => onLocationChange(value === ALL ? null : value)}
            >
              <SelectTrigger
                size="sm"
                className="w-44"
                aria-label={t('movements.filters.location')}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t('movements.filters.allLocations')}</SelectItem>
                {locations.map((location) => (
                  <SelectItem key={location.id} value={location.id}>
                    {location.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          <Select
            value={kind ?? ALL}
            onValueChange={(value) =>
              onKindChange(value === ALL ? null : (value as InventoryMovementKind))
            }
          >
            <SelectTrigger size="sm" className="w-40" aria-label={t('movements.filters.kind')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('movements.filters.allKinds')}</SelectItem>
              {KINDS.map((option) => (
                <SelectItem key={option} value={option}>
                  {t(`movements.kind.${option}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="px-6 pb-5">
        {query.isPending ? (
          <Skeleton className="h-40" />
        ) : query.error ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : movements.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('movements.empty')}</p>
        ) : (
          <MovementList
            storeId={storeId}
            movements={movements}
            {...(locations.length > 1 && !locationId ? { locationNames: names } : {})}
          />
        )}
        {query.hasNextPage ? (
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            disabled={query.isFetchingNextPage}
            onClick={() => void query.fetchNextPage()}
          >
            {t('movements.more')}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
